import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { playerDisplayName, type PlayerPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users } from "lucide-react";

// 150 -- players not on any of the school's team rosters for the season. This
// is the pool a coach builds rosters from. (Drag-to-roster is a follow-up; v1
// lists them with a link to open each player.)

export default function UnassignedPlayersPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const currentYear = String(new Date().getFullYear());
  const [season, setSeason] = useState(currentYear);
  const [players, setPlayers] = useState<PlayerPublic[]>([]);
  const [schoolName, setSchoolName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    api.getSchool(schoolId).then((r) => setSchoolName(r.school.name)).catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    api
      .getUnassignedPlayers(schoolId, season)
      .then((res) => setPlayers(res.players))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [schoolId, season]);

  return (
    <div className="flex flex-col gap-6" data-page="unassigned-players" data-state={loading ? "loading" : error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to={`/schools/${schoolId}/players`} className="hover:underline">{schoolName || "School"} players</Link>
            <span>/</span><span>Unassigned</span>
          </div>
          <h1 className="text-2xl font-semibold">Unassigned players</h1>
          <p className="text-muted-foreground">Players not yet on any team for the season.</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="season" className="text-sm text-muted-foreground">Season</label>
          <select
            id="season"
            title="Season"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="flex h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {[0, 1, 2].map((d) => {
              const y = String(Number(currentYear) - 1 + d);
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
      </div>

      <Card data-region="unassigned-list">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            {loading ? "…" : `${players.length} unassigned`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}
          {!loading && !error && players.length === 0 && (
            <p className="text-sm text-muted-foreground">Every active player is on a team for {season}.</p>
          )}
          {players.length > 0 && (
            <ul className="flex flex-col divide-y divide-border">
              {players.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2" data-player={p.id}>
                  <span className="text-sm font-medium">{playerDisplayName(p.first_name, p.surname)}</span>
                  <Link
                    to={`/players/${p.id}/edit`}
                    className={buttonVariants({ variant: "outline", size: "sm" })}
                  >
                    Open
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
